import { hc as _hc } from "hono/client";
import type { Hono } from "hono";
import type { ExtractSchema, MergeSchemaPath, Schema } from "hono/types";
import ky from "ky";
import type { APIResponse } from "@/shared/responses";
import type apiKeysHandler from "@/backend/handlers/api-keys";
import type billingHandler from "@/backend/handlers/billing";
import type callsHandler from "@/backend/handlers/calls";
import type campaignsHandler from "@/backend/handlers/campaigns";
import type contacts from "@/backend/handlers/contacts";
import type conversationLabels from "@/backend/handlers/conversation-labels";
import type dashboard from "@/backend/handlers/dashboard";
import type flowsHandler from "@/backend/handlers/flows";
import type inboxEventsHandler from "@/backend/handlers/inbox-events";
import type instagramHandler from "@/backend/handlers/instagram";
import type { instagramOAuthCallbackHandler, instagramWebhookHandler } from "@/backend/handlers/instagram";
import type integrationsHandler from "@/backend/handlers/integrations";
import type labels from "@/backend/handlers/labels";
import type mcpOAuthHandler from "@/backend/handlers/mcp-oauth";
import type organizationSettingsHandler from "@/backend/handlers/organization-settings";
import type postboxHandler from "@/backend/handlers/postbox";
import type push from "@/backend/handlers/push";
import type scheduledMessagesHandler from "@/backend/handlers/scheduled-messages";
import type rolesHandler from "@/backend/handlers/roles";
import type searchHandler from "@/backend/handlers/search";
import type settings from "@/backend/handlers/settings";
import type userHandler from "@/backend/handlers/user";
import type whatsappBusinessHandler from "@/backend/handlers/whatsapp-business";

export const instance = ky.create({
  retry: 0,
  timeout: 60000, // 60 seconds timeout for all API requests
  hooks: {
    beforeError: [
      async (error) => {
        const { response } = error;
        if (response?.body) {
          try {
            const body = await response.json<APIResponse & { error?: string }>();
            error.message = body.message || body.error || error.message;
          } catch {
            // If JSON parsing fails, keep original error
          }
        }
        return error;
      },
    ],
  },
});

/*
 * Typed API client, built per route group. Typing hc<AppType> over the whole app made TypeScript compute every
 * route's client type at once (about 65s of the type check, and slow in the editor). Each group below
 * mounts the same handlers at the same paths as src/backend/index.ts (under /api, and "/" for settings), so calls
 * like hc.api.campaigns[":id"].$get keep their types. A group that gains a handler in the backend needs it here too.
 */
type Mounted<Path extends string, App> =
  ExtractSchema<App> extends infer S extends Schema ? MergeSchemaPath<S, Path> : never;
type Client<S extends Schema> = ReturnType<typeof _hc<Hono<object, S, "/">>>;

const root = _hc(`/`) as unknown;
function group<S extends Schema>() {
  return root as Client<S>;
}

// Handlers mounted at "/" in the backend can add paths under any prefix
type RootMounted<Prefix extends string> =
  | Mounted<Prefix, typeof mcpOAuthHandler>
  | Mounted<Prefix, typeof apiKeysHandler>
  | Mounted<Prefix, typeof searchHandler>
  | Mounted<Prefix, typeof userHandler>;

export const hc = {
  api: {
    billing: group<Mounted<"/api/billing", typeof billingHandler>>().api.billing,
    calls: group<Mounted<"/api/calls", typeof callsHandler>>().api.calls,
    campaigns: group<Mounted<"/api/campaigns", typeof campaignsHandler>>().api.campaigns,
    contacts: group<Mounted<"/api/contacts", typeof contacts>>().api.contacts,
    conversations: group<Mounted<"/api/conversations", typeof conversationLabels>>().api.conversations,
    dashboard: group<Mounted<"/api/dashboard", typeof dashboard>>().api.dashboard,
    flows: group<Mounted<"/api/flows", typeof flowsHandler>>().api.flows,
    inbox: group<Mounted<"/api/inbox", typeof inboxEventsHandler>>().api.inbox,
    // infra.ts registers routes with app.get(...) statements rather than a chain, so Hono can't type them
    infra: (root as { api: { infra: unknown } }).api.infra,
    instagram: group<
      | Mounted<"/api/instagram", typeof instagramWebhookHandler>
      | Mounted<"/api/instagram", typeof instagramOAuthCallbackHandler>
      | Mounted<"/api/instagram", typeof instagramHandler>
    >().api.instagram,
    integrations: group<Mounted<"/api/integrations", typeof integrationsHandler>>().api.integrations,
    labels: group<Mounted<"/api/labels", typeof labels>>().api.labels,
    member: group<RootMounted<"/api">>().api.member,
    "org-settings": group<Mounted<"/api/org-settings", typeof organizationSettingsHandler>>().api["org-settings"],
    postbox: group<
      | Mounted<"/api/postbox", typeof postboxHandler>
      | Mounted<"/api/postbox/scheduled", typeof scheduledMessagesHandler>
    >().api.postbox,
    push: group<Mounted<"/api/push", typeof push>>().api.push,
    settings: group<Mounted<"/api/settings", typeof rolesHandler> | Mounted<"/api/settings", typeof settings>>().api.settings,
    "whatsapp-business": group<Mounted<"/api/whatsapp-business", typeof whatsappBusinessHandler>>().api["whatsapp-business"],
  },
  settings: group<Mounted<"/settings", typeof rolesHandler> | Mounted<"/settings", typeof settings>>().settings,
};
